import {
  createEntityAdapter,
  createSlice,
  createSelector,
  EntityState,
} from "@reduxjs/toolkit";

import { RootState } from "../../../app/store";
import { PTVerification } from "../../../api/physicalTechnicalVerifications";
import { fetchPTVerifications, upsertPTVerifications } from "./physicalTechnicalVerificationThunk";
import { RequestStatus } from "./assetsSlice";


export const ptVerificationsAdapter = createEntityAdapter<PTVerification, string>({
  selectId: (verification) => verification.verification_id,
});

export interface PTVerificationsState {
  ptVerifications: EntityState<PTVerification, string>;
  verificationIdsByChargeId: Record<string, string[]>; 
  status: RequestStatus;
  error?: string | null;
  caseId?: string;
}

const initialState: PTVerificationsState = {
  ptVerifications: ptVerificationsAdapter.getInitialState(),
  verificationIdsByChargeId: {},
  status: "idle",
  error: null,
  caseId: undefined,
};

const appendVerificationToMap = (
  state: PTVerificationsState,
  verification: PTVerification
) => {
  const { parent_entity_id, verification_id } = verification;
  if (!parent_entity_id) return;

  if (!state.verificationIdsByChargeId[parent_entity_id]) {
    state.verificationIdsByChargeId[parent_entity_id] = [];
  }
  if (!state.verificationIdsByChargeId[parent_entity_id].includes(verification_id)) {
    state.verificationIdsByChargeId[parent_entity_id].push(verification_id);
  }
};

const ptVerificationsSlice = createSlice({
  name: "physicalTechnicalVerifications",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPTVerifications.pending, (state) => {
        state.status = "loading";
        state.error = null;

        ptVerificationsAdapter.removeAll(state.ptVerifications);
        state.verificationIdsByChargeId = {}; 
      })

      .addCase(fetchPTVerifications.fulfilled, (state, action) => {
        state.status = "success";
        state.caseId = action.meta.arg;

        ptVerificationsAdapter.setAll(state.ptVerifications, action.payload.verifications);

        state.verificationIdsByChargeId = {};
        action.payload.verifications.forEach((v) => {
         appendVerificationToMap(state, v);
        });
      })

      .addCase(fetchPTVerifications.rejected, (state, action) => {
        state.status = "failed";
        state.error ="Failed to fetch physical technical verifications";
      })
      

      .addCase(upsertPTVerifications.fulfilled, (state, action) => {
        const verifications = action.meta.arg.request.verifications;
        ptVerificationsAdapter.upsertMany(state.ptVerifications, verifications);
        verifications.forEach((v) => {
          appendVerificationToMap(state, v);
        });
      })
  },
});

export const physicalTechnicalVerificationsReducer =ptVerificationsSlice.reducer;

const ptVerificationsSelectors = ptVerificationsAdapter.getSelectors<RootState>(
    (state) =>
      state.physicalTechnicalVerifications.ptVerifications,
  );

export const {
  selectAll: selectAllPTVerifications,
  selectById: selectPTVerificationById,
  selectEntities: selectPTVerificationEntities,
  selectIds: selectPTVerificationIds,
  selectTotal: selectPTVerificationCount,
} = ptVerificationsSelectors;

export const selectPTVerificationsStatus = (
  state: RootState,
) => state.physicalTechnicalVerifications.status;

export const selectPTVerificationsError = (state: RootState) => state.physicalTechnicalVerifications.error;

export const selectPTVerificationsByChargeIds = createSelector(
  [
    ptVerificationsSelectors.selectEntities,
    (state: RootState) => state.physicalTechnicalVerifications.verificationIdsByChargeId,
    (_state: RootState, chargeIds: string[]) => chargeIds
  ],
  (verificationEntities, verificationIdsMap, chargeIds) => {
    const uniqueVerifications: PTVerification[] = [];
    if (!chargeIds) return uniqueVerifications;

    const addedIds: string[] = [];

    chargeIds.forEach((chargeId) => {
      const mappedVerificationIds = verificationIdsMap[chargeId] || [];
      
      mappedVerificationIds.forEach((verificationId) => {
        if (!addedIds.includes(verificationId)) {
          addedIds.push(verificationId);
          
          const verification = verificationEntities[verificationId];
          if (verification) {
            uniqueVerifications.push(verification);
          }
        }
      });
    });

    return uniqueVerifications;
  }
);